<template lang="pug">
  v-dialog(v-model="showNewCalendarEvent"
    :fullscreen="$vuetify.breakpoint.mdAndDown"
    max-width="1000"
    persistent)
    v-snackbar( v-model="snackbar" color="red darken-2" ) {{ snackBarMessage }}
      template( v-slot:action="{attrs}")
        v-btn(text v-bind="attrs"
          @click="snackbar = false") {{ $t('buttons.closeButtonLabel') }}
    v-card.d-flex.flex-column(flat)
      v-card-title.headline {{ $t('calendarEventDialog.dialogTitle') }}
      v-form(v-model="valid" ref="form" @submit.prevent="onSaveButtonSubmit" lazy-validation)
        v-card-text.pb-1.flex-shrink-0.flex-grow-1.card-body
          v-row
            v-col(cols="12" sm="12" md="6" lg="6" xl="6")
              v-card(flat)
              v-card-text.pt-0.text-center {{ $t('calendarEventDialog.datePickerHeading') }}
              v-card-text.d-flex.justify-center.align-center.pa-0.ma-0
                v-date-picker( v-model="dateSelection"
                  @change="onDateSelectionChange"
                  scrollable
                  first-day-of-week="1"
                  show-week
                  :disabled="saveInProgress"
                  multiple
                  no-title
                  :full-width="$vuetify.breakpoint.smAndDown"
                  prev-icon="fas fa-chevron-left"
                  next-icon="fas fa-chevron-right")
            v-col.mt-4.mt-md-0.mt-lg-0.mt-xl-0.pa-0.pa-md-3(cols="12" sm="12" md="6" lg="6" xl="6")
              v-card(flat)
                v-card-text.pt-0.text-center {{ $t('calendarEventDialog.newEventOptionsHeading') }}
                v-card-text.pa-0.boarder-list
                  v-select( :loading="fetchEventKindInProgress"
                    :disabled="fetchEventKindInProgress || saveInProgress"
                    v-model="event"
                    :items="events"
                    :rules="selectRules"
                    item-text="name"
                    append-icon="fal fa-chevron-down"
                    :label=" $t('calendarEventDialog.vSelectLabel') "
                    return-object
                    outlined)
                v-alert( v-if="ordersExisting"
                  color="red" text ) {{ $t('calendarEventDialog.ordersExists') }}
                v-checkbox( v-if="ordersExisting"
                  on-icon="fas fa-check-square"
                  off-icon="fal fa-square"
                  input-value="1"
                  :disabled="saveInProgress"
                  :label=" $t('calendarEventDialog.checkboxLabel') "
                  @change="onCheckBoxChange")
        v-divider
        v-card-actions
          v-spacer
          v-btn( @click="onCloseButtonClick" text color="primary" :disabled="saveInProgress" ) {{ $t('buttons.closeButtonLabel') }}
          v-btn(color="primary"
            type="submit"
            :disabled="saveInProgress || verificationInProgress || !valid"
            :loading="saveInProgress || verificationInProgress") {{ $t('buttons.saveButtonLabel') }}

</template>

<script lang="ts">
import Vue from "vue";
import {Component, Prop, Watch} from "vue-property-decorator";
import {Action} from "vuex-class";
import {CREATE_CALENDAR_EVENT_FOR_DAY, FETCH_CALENDAR_EVENTS_LIST, FETCH_NUMBER_OF_ORDERS} from "@/store/action-types";
import {CalendarEvent} from "vuetify";
import {NewCalendarEventPayload, NumberOfOrdersPayload, NumberOfOrdersWithCancelTokenPayload} from "@/types/payload";
import {CalendarEventForDayResponse, NumberOfOrdersResponse} from "@/types/service-response";
import axios, {AxiosError, CancelTokenSource} from "axios";
import i18n from "@/i18n";

@Component({
  components: {}
})

export default class CalendarEventDialog extends Vue {
  $refs!: {
    picker: Vue & {
      prev(): boolean,
      next(): boolean,
    },
    form: Vue & {
      validate(): boolean
      resetValidation(): boolean
    }
  }

  @Prop({type: Boolean, default: false}) showNewCalendarEvent!: boolean

  @Action(FETCH_CALENDAR_EVENTS_LIST) fetchCalendarEventsList!: { (): Promise<CalendarEvent[]> }
  @Action(CREATE_CALENDAR_EVENT_FOR_DAY) createCalendarEventForDay!: { (newEventData: NewCalendarEventPayload): Promise<CalendarEventForDayResponse> }
  @Action(FETCH_NUMBER_OF_ORDERS) fetchNumberOfOrders!: { (payload: NumberOfOrdersWithCancelTokenPayload): Promise<NumberOfOrdersResponse> }

  private snackbar: boolean = false
  private snackbarColor: string = ''
  private snackBarMessage: string = ''
  private saveInProgress: boolean = false
  private fetchInProgress: boolean = false
  private fetchEventKindInProgress: boolean = false
  private verificationInProgress: boolean = false

  private event: CalendarEvent | null = null
  // noinspection JSMismatchedCollectionQueryUpdate
  private events: CalendarEvent[] = []
  private toBeDeliveredCount: number = 0
  private ordersExisting: boolean = false
  private cancelExistingOrders: boolean = true
  private wasCreated: boolean = false

  private dateSelection: string[] = []
  private pickedDates: string[] = []
  private date: string = ''
  private kind: string = ''
  private cancelToken: CancelTokenSource | null = null

  private valid: boolean = false

  private selectRules: Array<(v: string) => string | boolean> = [
    (v: string) => !!v || i18n.t('calendarEventDialog.selectValidationText').toString()
  ]

  @Watch('dateSelection')
  onDateSelectionChange(newVal: string[]) {
    if (newVal.length) {
      this.pickedDates = newVal

      this.fetchOrdersData()
    }
  }

  async fetchEventsData(): Promise<void> {

    this.fetchEventKindInProgress = true

    try {
      this.events = await this.fetchCalendarEventsList()
    } catch (e) {
      this.snackbar = true
      this.snackbarColor = 'error'
      this.snackBarMessage = this.$t('errors.fetchDataError').toString()
    } finally {
      this.fetchEventKindInProgress = false
    }
  }

  prev(): void {
    this.$refs.picker.prev()
  }

  next(): void {
    this.$refs.picker.next()
  }

  onCloseButtonClick(): void {
    this.closeDialog()
  }

  closeDialog(): void {
    this.$emit('closeDialog', this.wasCreated)
  }

  onSaveButtonSubmit(): void {
    if (this.$refs.form.validate()) {
      if (this.pickedDates.length != 0 ) {
        this.addData()
      } else {
        this.snackbar = true
        this.snackbarColor = 'error'
        this.snackBarMessage = this.$t('calendarEventDialog.noDaysPicked').toString()
      }
    }
  }

  onCheckBoxChange(event: boolean) {
    if (event) {
      this.cancelExistingOrders = true
    } else if (!event) {
      this.cancelExistingOrders = false
    }
  }

  async addData(): Promise<void> {

    this.saveInProgress = true

    try {

      for (let i = 0; i < this.pickedDates.length; i++) {
        this.date = this.pickedDates[i]

        if (this.event!.name == 'Prázdniny') {
          this.kind = 'holiday'
        } else if (this.event!.name == 'Voľný deň') {
          this.kind = 'free_day'
        }

        let newEventData: NewCalendarEventPayload = {
          kind: this.kind,
          date: this.date,
          cancelActiveReservations: this.cancelExistingOrders
        }

        try {
          await this.createCalendarEventForDay(newEventData)
          this.wasCreated = true
        } catch (e) {
          this.snackbar = true
          this.snackbarColor = 'error'
          this.snackBarMessage = this.$t('errors.saveErrorMessage').toString()
        }
      }

      this.closeDialog()
    } catch (e) {
      this.snackbar = true
      this.snackbarColor = 'error'
      this.snackBarMessage = this.$t('errors.saveErrorMessage').toString()
    } finally {
      this.saveInProgress = false
    }
  }

  async fetchOrdersData(): Promise<void> {

    if (this.cancelToken) {
      this.cancelToken.cancel()
    }

    this.cancelToken = axios.CancelToken.source()

    let pickedDates: NumberOfOrdersPayload = {
      dates: this.pickedDates
    }

    let payload: NumberOfOrdersWithCancelTokenPayload = {
      payload: pickedDates,
      cancelToken: this.cancelToken
    }

    try {
      this.verificationInProgress = true
      let response = await this.fetchNumberOfOrders(payload)
      this.toBeDeliveredCount = response.toBeDeliveredCount
      if (this.toBeDeliveredCount > 0) {
        this.ordersExisting = true
      }
      this.cancelToken = null
    } catch (e) {
      if ((e as any).isCancel(e )) {

      } else {
        this.snackbar = true
        this.snackbarColor = 'error'
        this.snackBarMessage = this.$t('errors.fetchDataError').toString()
      }
    } finally {
      this.verificationInProgress = false
    }
  }

  mounted(): void {
    this.fetchEventsData()
  }
}
</script>

<style>

</style>